/**
 * Вход команды Skrezo — единый контур гейтвея: POST /auth/login
 * (телефон+пароль). Платформенный доступ подтверждает флаг
 * `isPlatformAdmin` в ответе — без него в админку не пускаем,
 * даже если пароль верный (обычный клиент клиники).
 *
 * Мок-режим (пустой VITE_API_BASE_URL): любой логин/пароль проходит;
 * зарезервированный `fail@skrezo.kg` рисует состояние 401 без BE.
 */

import {apiEnabled, http, HttpError, writeTeamRefresh} from '@services/utils';
import {toE164} from '@utils/phone';

import type {TeamLoginRequest, TeamLoginResponse} from './loginTeam.schema';

const FAKE_LATENCY_MS = 400;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Форма живого ответа (ClientTokenResponse гейтвея) — берём только нужное */
type GatewayLogin = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  account: {id: string; fullName?: string | null; phone?: string};
  isPlatformAdmin?: boolean;
  mustChangePassword?: boolean;
};

export const loginTeam = async (req: TeamLoginRequest): Promise<TeamLoginResponse> => {
  if (!apiEnabled) {
    await sleep(FAKE_LATENCY_MS);
    if (req.login.trim().toLowerCase() === 'fail@skrezo.kg') {
      throw new Error('invalid_credentials');
    }
    return {
      accessToken: `mock-team-token-${Date.now()}`,
      refreshToken: `mock-team-refresh-${Date.now()}`,
      expiresIn: 3600,
      // Мок: пароль «temp1234» — как временный, ведёт на смену пароля
      mustChangePassword: req.password === 'temp1234',
      user: {id: 'team-1', fullName: 'Команда Skrezo', login: req.login},
    };
  }

  const data = await http<GatewayLogin>('auth/login', {
    method: 'POST',
    body: {phone: toE164(req.login), password: req.password},
    // Гейтвей говорит на camelCase — snake-конверсию не применяем
    skipCaseConversion: true,
  });

  // Пароль верный, но аккаунт не из команды — в платформенную зону нельзя
  if (data.isPlatformAdmin !== true) {
    throw new HttpError('no_platform_access', 403, data);
  }

  // Refresh — в хранилище: http продлевает access сам, без повторного входа
  writeTeamRefresh(data.refreshToken);

  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresIn: data.expiresIn,
    mustChangePassword: data.mustChangePassword === true,
    user: {
      id: data.account.id,
      fullName: data.account.fullName ?? null,
      login: req.login,
    },
  };
};
